import { inject, Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { getAllAssets } from '@app/core/entities/reducers';
import {
  Allocation,
  Asset,
  BacktestResult,
  CsvExportAllocation,
  CsvExportPosition,
  Project,
  TranslationObject,
} from '@app/core/models';
import { CsvLineNumbersWithErrors } from '@app/core/models/csv-line-numbers-with-errors';
import { CsvLineNumbersWithWarnings } from '@app/core/models/csv-line-numbers-with-warnings';
import { downloadFile } from '@app/core/tools/file-saver';
import { SnackBarService } from '@app/shared/services';
import { FormToModelAdapter } from '@app/simulation/adapters/form-model.adapter';
import * as fromRoot from '@app/store/reducers';
import { Parser } from '@json2csv/plainjs';
// adding .js to fix imports issues https://github.com/apache/echarts/issues/21205#issuecomment-3207578314
import { Json2CSVBaseOptions } from '@json2csv/plainjs/BaseParser.js';
import { unwind } from '@json2csv/transforms';
import { Store } from '@ngrx/store';
import { orderBy } from 'lodash';
import { firstValueFrom } from 'rxjs';

import { csvExportAllocationFields, csvExportProjectFields } from '../const/csv-export';
import { AvailableLang } from '../const/i18n';
import { isAssetCash, isAssetCredit } from '../tools/smart-risk.tools';

@Injectable({
  providedIn: 'root',
})
export class CsvExportService {
  private readonly _store = inject<Store<fromRoot.State>>(Store);
  private readonly _snackBarService = inject(SnackBarService);

  exportProjectAsCsv(project: Project, assets: Asset[], currentLang: AvailableLang): void {
    const {
      id,
      userId,
      name,
      currency,
      creditRate,
      financialProfile,
      riskProfile,
      hedge: hedging,
      allocations,
      backtestResults,
      backtestParameters,
    } = project;
    const asset = assets.find(assetItem => assetItem.id === backtestParameters.benchmark.positions[0].instrumentId);

    const csvExportProject = {
      id,
      userId,
      name,
      currency,
      creditRate,
      hedging,
      financialProfile,
      allocations: this._getCsvAllocationData(allocations, backtestResults, assets, currentLang),
      riskProfile,
      backtestParameters: { ...backtestParameters, assetName: asset.name[currentLang] },
    };
    /*
    const transforms = [
      unwind({
        paths: ['allocations', 'allocations.csvData', 'allocations.backtestResults'],
        blankOut: true,
      }),
    ];
    */

    const opts: Readonly<Omit<Json2CSVBaseOptions<object, object>, 'ndjson'>> = {
      delimiter: ';',
      fields: csvExportProjectFields,
      transforms: [
        unwind({
          paths: ['allocations', 'allocations.csvData', 'allocations.backtestResults'],
          blankOut: true,
        }),
      ],
      withBOM: true,
    };

    const parser = new Parser(opts);
    const csv = parser.parse(csvExportProject);
    downloadFile(csv, 'text/csv', `${project.name}.csv`);
  }

  exportAllocationAsCsv(allocation: Allocation, assets: Asset[], currentLang: AvailableLang): void {
    const opts: Readonly<Omit<Json2CSVBaseOptions<object, object>, 'ndjson'>> = {
      delimiter: ';',
      fields: csvExportAllocationFields,
      withBOM: true,
    };

    const parser = new Parser(opts);
    const csv = parser.parse(
      assets.map(asset => {
        const allocationPosition = allocation?.positions.find(pos => pos.instrumentId === asset.id);
        return {
          assetId: asset.id,
          assetName: asset.name[currentLang] ?? '-',
          assetWeight: allocationPosition?.positionValue ?? 0,
        };
      })
    );
    // when opening a simulation in creation without setting any value in the first default allocation
    // the allocation name is not set, this is why we generate a default allocationName
    const defaultAllocationName = FormToModelAdapter.getAllocationNameFromIndex(0);
    const fileName = allocation?.name ?? defaultAllocationName;
    downloadFile(csv, 'text/csv', `${fileName}.csv`);
  }

  async importAllocationFromCsv(
    csvData: string,
    allocationControls: UntypedFormGroup['controls'],
    simulationValidAssetIds: number[]
  ): Promise<void> {
    let skipHeader = true;

    const csvLines = csvData.split('\n');

    const allKnownAssetsIds = await firstValueFrom(this._store.select(getAllAssets)).then(allAssets =>
      allAssets.map(({ id }) => id)
    );
    const { errors } = this._getCsvLineNumbersWithErrors(csvLines, simulationValidAssetIds, allKnownAssetsIds);
    const invalidLinesNumbers = Object.values(errors).flatMap(_ => _);

    this._showErrorMessageIfErrors(errors);

    csvLines.forEach((csvLine, index) => {
      const lineNumber = index + 1;
      if (skipHeader === true) {
        skipHeader = false;
        return;
      }

      // if a line has been marked as invalid, should not be processed
      // this will avoid double validations
      if (invalidLinesNumbers.includes(lineNumber)) {
        return;
      }

      const csvElements = csvLine.split(';');

      if (csvElements[0].trim() !== '') {
        const assetId = +csvElements[0];
        const weight = parseInt(csvElements[2]);
        for (const [key, value] of Object.entries(allocationControls)) {
          if (this._getControlName(value) === `${assetId}`) {
            allocationControls[key].setValue(weight);
            break;
          }
        }
      }
    });
  }

  private _getCsvAllocationData(
    allocations: Allocation[],
    backtestResults: BacktestResult[],
    assets: Asset[],
    currentLang: AvailableLang
  ): CsvExportAllocation[] {
    return backtestResults.map((backtestResultItem, index) => {
      let positions: CsvExportPosition[] = [];
      const allocation = allocations[index] ? allocations[index] : undefined;
      const allocationResult = allocation
        ? {
            ...allocation.exAnteAnalysis.allocationResult,
            normalVar: -allocation.exAnteAnalysis.allocationResult.normalVar,
          }
        : undefined;
      const csvDataLength = allocation
        ? Math.max(
            ...[
              allocation.positions.length,
              allocation.exAnteAnalysis.liquidity.length,
              backtestResultItem.portfolioReturns.length,
            ]
          )
        : backtestResultItem.portfolioReturns.length;

      if (allocation) {
        const positionIds = { cash: 0, credit: 1 };
        positions = allocation.positions.map(position => {
          const asset = assets.find(assetItem => assetItem.id === position.instrumentId);
          let positionId = asset.id + Object.keys(positionIds).length;
          if (isAssetCash(asset)) {
            positionId = positionIds.cash;
          }
          if (isAssetCredit(asset)) {
            positionId = positionIds.credit;
          }
          return {
            assetName: asset ? asset.name[currentLang] : 'Asset not found',
            positionId,
            positionValue: position.positionValue,
          };
        });
      }

      const sortedPosition = orderBy(positions, 'positionId', 'asc');
      const csvData = Array.from({ length: csvDataLength }, (_, key) => {
        const position = sortedPosition[key] ? sortedPosition[key] : undefined;
        let liquidity;
        let backtestResult;

        if (allocation?.exAnteAnalysis.liquidity[key]) {
          liquidity = {
            liquidityLabel: allocation.exAnteAnalysis.liquidity[key].label,
            liquidityValue: allocation.exAnteAnalysis.liquidity[key].value,
          };
        }
        if (backtestResultItem.portfolioReturns[key]) {
          backtestResult = {
            backtestResultDate: backtestResultItem.portfolioReturns[key].date,
            backtestResultValue: backtestResultItem.portfolioReturns[key].value,
          };
        }

        return {
          ...position,
          ...liquidity,
          ...backtestResult,
        };
      });

      return {
        id: backtestResultItem.allocationId,
        name: allocation ? allocation.name : 'Benchmark',
        allocationResult,
        backtestMaxDrawDown: -backtestResultItem.maxDrawDown,
        backtestHistoricalReturn: backtestResultItem.historicalReturn,
        backtestVolatility: backtestResultItem.volatility,
        csvData,
      };
    });
  }

  private _getControlName(c: AbstractControl): string | null {
    const formGroup = c.parent.controls;
    return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
  }

  private _getCsvLineNumbersWithErrors(
    csvLines: string[],
    allSimulationValidAssets: number[],
    allKnownAssetsIds: number[]
  ): { errors: CsvLineNumbersWithErrors, warnings: CsvLineNumbersWithWarnings } {
    const errors: CsvLineNumbersWithErrors = {
      invalidWeightError: [],
      negativeValueForNonCreditAssetError: [],
      positiveValueForCreditAssetError: [],
      invalidAssetClassIdError: [],
      invalidLineFormatError: []
    };
    const warnings: CsvLineNumbersWithWarnings = {
      invalidAssetClassWarning: []
    }

    csvLines.forEach((line, index) => {
      const lineNumber = index + 1;
      const isNotHeader = index !== 0;
      const cells = line.split(';');
      if (cells.length !== 3) {
        errors.invalidLineFormatError.push(lineNumber);
      }
      if (isNotHeader) {
        if (
          this._isKnownAssetClassNotValidForSimulationWithZeroWeight(cells, allSimulationValidAssets, allKnownAssetsIds)
        ) {
          warnings.invalidAssetClassWarning.push(lineNumber);
        } else {
          const isValidAssetClassId = this._isValidAssetClassId(cells[0], allSimulationValidAssets);
          const isValidWeight = this._isValidWeight(cells[2]);
          if (!isValidAssetClassId) {
            errors.invalidAssetClassIdError.push(lineNumber);
          }
          if (!isValidWeight) {
            errors.invalidWeightError.push(lineNumber);
          }
          if (isValidAssetClassId && isValidWeight) {
            if (!this._isPositiveOrCredit(cells[0], cells[2])) {
              errors.negativeValueForNonCreditAssetError.push(lineNumber);
            }
            if (!this._isNegativeOrNonCredit(cells[0], cells[2])) {
              errors.positiveValueForCreditAssetError.push(lineNumber);
            }
          }
        }
      }
    });

    return { errors, warnings };
  }

  private _isKnownAssetClassNotValidForSimulationWithZeroWeight(
    cells: string[],
    allSimulationValidAssets: number[],
    allKnownAssetsIds: number[]
  ): boolean {
    const weight = parseInt(cells[2], 10);
    const idNumber = +cells[0];
    return weight === 0 && !allSimulationValidAssets.includes(idNumber) && allKnownAssetsIds.includes(idNumber);
  }

  private _isValidAssetClassId(id: string, allAssetsIds: number[]): boolean {
    const idNumber = +id;
    return !Number.isNaN(idNumber) && allAssetsIds.includes(idNumber);
  }

  private _isValidWeight(value: string): boolean {
    const weight = parseInt(value, 10);
    return !Number.isNaN(weight);
  }

  private _isPositiveOrCredit(id: string, weight: string): boolean {
    const assetId = +id;
    const weightNumber = +weight;
    if (!isAssetCredit({ id: assetId })) {
      return weightNumber >= 0;
    }
    return true;
  }

  private _isNegativeOrNonCredit(id: string, weight: string): boolean {
    const assetId = +id;
    const weightNumber = +weight;
    if (isAssetCredit({ id: assetId })) {
      return weightNumber <= 0;
    }
    return true;
  }

  private _showErrorMessageIfErrors(errors: CsvLineNumbersWithErrors): void {
    const hasAtLeastOneError = Object.keys(errors).some(key => errors[key].length > 0);
    if (hasAtLeastOneError) {
      const errorMessages: TranslationObject[] = [];
      if (errors.invalidLineFormatError.length > 0) {
        errorMessages.push({
          key: 'errors.import.invalid-line-format',
          params: { lineNumbers: errors.invalidLineFormatError.join(',') },
        });
      }
      if (errors.invalidAssetClassIdError.length > 0) {
        errorMessages.push({
          key: 'errors.import.invalid-asset-class-id',
          params: { lineNumbers: errors.invalidAssetClassIdError.join(',') },
        });
      }
      if (errors.invalidWeightError.length > 0) {
        errorMessages.push({
          key: 'errors.import.invalid-weight',
          params: { lineNumbers: errors.invalidWeightError.join(',') },
        });
      }
      if (errors.negativeValueForNonCreditAssetError.length > 0) {
        errorMessages.push({
          key: 'errors.import.negative-value-for-non-credit-asset',
          params: { lineNumbers: errors.negativeValueForNonCreditAssetError.join(',') },
        });
      }
      if (errors.positiveValueForCreditAssetError.length > 0) {
        errorMessages.push({
          key: 'errors.import.positive-value-for-credit-asset',
          params: { lineNumbers: errors.positiveValueForCreditAssetError.join(',') },
        });
      }
      this._snackBarService.openSnackBar('error', errorMessages, undefined, 10000);
    }
  }
}
